/**
 * The sign-in steps beyond the email code (design "Round 2: sign-in steps"): the password step (email chip, show/hide,
 * generic error, lockout), the two-step challenge (six cells or a backup code, "Trust this device for 30 days"),
 * password reset (code → new password with live hints and a strength meter → Done) and the passkey prompt (waiting,
 * cancelled, not found). Renders SessionViewModel's `flow`; SignInForm picks the step.
 */
import { AccountSecurityLogic, type PasswordProblem, type PasswordStrength, type SessionViewModel, type SignInFlow, type SignInError } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useState, type ReactNode } from "react";
import { Avatar, Banner, Button, Checkbox, CodeCells, Icon, Spinner, Stack, Text, TextField } from "../primitives/index.ts";
import { useT, type Translate } from "../i18n/I18nProvider.tsx";

type Busy = SessionViewModel["state"]["busy"];

export function flowErrorText(t: Translate, error: SignInError, minutes: number | undefined, problem?: PasswordProblem): string | undefined {
  switch (error) {
    case "wrongPassword":
      return t("auth.error.wrongPassword");
    case "locked":
      return t("auth.error.locked", { minutes: minutes ?? AccountSecurityLogic.LOCKOUT_MIN });
    case "twoStepCode":
      return t("auth.error.twoStepCode");
    case "backupCode":
      return t("auth.error.backupCode");
    case "password":
      return problem === undefined ? undefined : passwordProblemText(t, problem);
    default:
      return undefined;
  }
}

function passwordProblemText(t: Translate, problem: PasswordProblem): string {
  switch (problem) {
    case "tooShort":
      return t("auth.error.passwordShort");
    case "tooLong":
      return t("auth.error.passwordLong");
    case "containsEmail":
      return t("auth.error.passwordEmail");
    case "tooCommon":
      return t("auth.error.passwordCommon");
    case "leaked":
      return t("auth.error.passwordLeaked");
  }
}

/** Error text in status.danger.fg (4.5:1 at 13 px on white and on dark elevated surfaces). */
export function StepError({ text, testID = "sign-in-error" }: { text: string | undefined; testID?: string }) {
  if (text === undefined) return null;
  return (
    <Stack direction="horizontal" gap="2" align="start" testID={testID}>
      <Icon name="alert" size="icon.sm" color="status.danger.fg" />
      <Text variant="footnote" color="status.danger.fg">
        {text}
      </Text>
    </Stack>
  );
}

function Heading({ title, body }: { title: string; body?: string }) {
  return (
    <Stack gap="1">
      <Text variant="headline">{title}</Text>
      {body === undefined ? null : <Text color="text.secondary">{body}</Text>}
    </Stack>
  );
}

/** The email carried over from the first step, like Google's account chip. */
function EmailChip({ email }: { email: string }) {
  return (
    <Stack direction="horizontal" align="center" gap="2" padding="1" radius="full" border="border.subtle" testID="sign-in-email-chip">
      <Avatar name={email} size="avatar.xs" />
      <Text variant="footnote" weight="medium" selectable>
        {email}
      </Text>
    </Stack>
  );
}

function Links({ children }: { children: ReactNode }) {
  return (
    <Stack direction="horizontal" align="center" justify="between" wrap>
      {children}
    </Stack>
  );
}

export function PasswordStep({ vm, flow, busy, error }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "password" }>; busy: Busy; error: string | undefined }) {
  const t = useT();
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const submit = () => void vm.signInWithPassword(password);
  return (
    <Stack gap="4" testID="sign-in-password-step">
      <Stack gap="2" align="start">
        <Text variant="headline">{t("auth.password.title")}</Text>
        <EmailChip email={flow.email} />
      </Stack>
      {flow.locked ? <Banner testID="sign-in-locked" tone="warning" icon="clock" title={error ?? t("auth.error.locked", { minutes: AccountSecurityLogic.LOCKOUT_MIN })} message={t("auth.password.lockedHint")} /> : null}
      <Stack gap="2">
        <TextField
          label={t("auth.password.label")}
          value={password}
          onChange={setPassword}
          secure={!shown}
          contentType="currentPassword"
          disabled={flow.locked || busy === "password"}
          autoFocus
          onSubmit={submit}
          {...(!flow.locked && error !== undefined ? { error } : {})}
          trailingAction={{
            icon: shown ? "eyeOff" : "eye",
            label: shown ? t("auth.password.hide") : t("auth.password.show"),
            onPress: () => setShown((s) => !s),
            testID: "sign-in-password-toggle",
          }}
          testID="sign-in-password"
        />
        <Stack direction="horizontal">
          <Button label={t("auth.password.forgot")} variant="ghost" size="sm" disabled={busy !== undefined && busy !== "sendingCode"} loading={busy === "sendingCode"} onPress={() => void vm.forgotPassword()} testID="sign-in-forgot" />
        </Stack>
      </Stack>
      {flow.locked ? (
        <Button label={t("auth.password.useCode")} icon="mail" variant="primary" size="lg" loading={busy === "sendingCode"} onPress={() => void vm.emailCodeInstead()} testID="sign-in-code-instead" />
      ) : (
        <Button
          label={busy === "password" ? t("auth.signingIn") : t("auth.code.submit")}
          variant="primary"
          size="lg"
          loading={busy === "password"}
          disabled={password === ""}
          onPress={submit}
          testID="sign-in-password-submit"
        />
      )}
      <Links>
        <Button label={t("auth.code.changeEmail")} variant="ghost" size="sm" onPress={() => vm.backToStart()} testID="sign-in-change-email" />
        {flow.locked ? null : (
          <Button label={t("auth.password.useCode")} variant="ghost" size="sm" disabled={busy !== undefined} onPress={() => void vm.emailCodeInstead()} testID="sign-in-code-instead-link" />
        )}
      </Links>
    </Stack>
  );
}

export function TwoFactorStep({ vm, flow, busy, error }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "twoFactor" }>; busy: Busy; error: string | undefined }) {
  const t = useT();
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const verifying = busy === "verifying";
  const switchTo = (b: boolean) => {
    setCode("");
    setBackup("");
    vm.setBackupMode(b);
  };
  const text = flow.text === true && !flow.backup;
  const textTo = flow.textTo ?? "";
  return (
    <Stack gap="4" testID="sign-in-two-step">
      <Heading
        title={flow.backup ? t("auth.twoStep.backupTitle") : text ? t("phone.signInTitle") : t("auth.twoStep.title")}
        body={flow.backup ? t("auth.twoStep.backupBody") : text ? t("phone.signInBody", { phone: textTo }) : t("auth.twoStep.body")}
      />
      {flow.backup ? (
        <TextField
          label={t("auth.twoStep.backupLabel")}
          value={backup}
          onChange={setBackup}
          mono
          autoFocus
          disabled={verifying}
          onSubmit={() => void vm.verifySecondFactor(backup)}
          {...(error === undefined ? {} : { error })}
          testID="sign-in-backup-code"
        />
      ) : (
        <Stack gap="2">
          <CodeCells
            value={code}
            onChange={setCode}
            onComplete={(c) => void vm.verifySecondFactor(c)}
            label={t("auth.twoStep.codeLabel")}
            cellLabel={(n) => t("auth.twoStep.digit", { n })}
            error={error !== undefined}
            disabled={verifying}
            autoFocus
            testID="sign-in-totp"
          />
          <StepError text={error} />
        </Stack>
      )}
      <Stack gap="0.5">
        <Checkbox label={t("auth.twoStep.trust")} value={flow.trustDevice} onChange={(v) => vm.setTrustDevice(v)} testID="sign-in-trust-device" />
        <Text variant="caption" color="text.tertiary">
          {t("auth.twoStep.trustHint")}
        </Text>
      </Stack>
      <Button
        label={verifying ? t("auth.twoStep.verifying") : t("auth.twoStep.submit")}
        variant="primary"
        size="lg"
        loading={verifying}
        disabled={flow.backup ? AccountSecurityLogic.normalizeCode(backup).length < 6 : code.length !== 6}
        onPress={() => void vm.verifySecondFactor(flow.backup ? backup : code)}
        testID="sign-in-two-step-submit"
      />
      <Links>
        <Button
          label={flow.backup || text ? t("auth.twoStep.useApp") : t("auth.twoStep.useBackup")}
          variant="ghost"
          size="sm"
          onPress={() => {
            if (text) {
              setCode("");
              void vm.setTextMode(false);
            } else switchTo(!flow.backup);
          }}
          testID="sign-in-two-step-switch"
        />
        {flow.textOffered === true && !text ? (
          <Button
            label={t("phone.twoStepTextMe", { phone: textTo })}
            variant="ghost"
            size="sm"
            loading={busy === "sendingCode"}
            onPress={() => {
              setCode("");
              void vm.setTextMode(true);
            }}
            testID="sign-in-two-step-text"
          />
        ) : null}
        {text ? (
          <Button
            label={t("phone.resend")}
            variant="ghost"
            size="sm"
            loading={busy === "sendingCode"}
            onPress={() => void vm.setTextMode(true)}
            testID="sign-in-two-step-text-resend"
          />
        ) : null}
        <Button label={t("auth.reset.back")} variant="ghost" size="sm" onPress={() => vm.backToStart()} testID="sign-in-back" />
      </Links>
      {flow.backup ? (
        <Text variant="caption" color="text.tertiary">
          {t("auth.twoStep.lostAccess")}
        </Text>
      ) : null}
    </Stack>
  );
}

export function ResetCodeStep({ vm, flow, busy, error }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "resetCode" }>; busy: Busy; error: string | undefined }) {
  const t = useT();
  const [code, setCode] = useState("");
  const submit = () => vm.submitResetCode(code);
  return (
    <Stack gap="4" testID="sign-in-reset-code">
      <Heading title={t("auth.reset.title")} body={t("auth.code.sentTo", { email: flow.email })} />
      <TextField
        label={t("auth.reset.codeLabel")}
        value={code}
        onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
        keyboard="oneTimeCode"
        autoFocus
        onSubmit={submit}
        {...(error === undefined ? {} : { error })}
        testID="sign-in-reset-code-field"
      />
      <Button label={t("auth.continue")} variant="primary" size="lg" disabled={code.length !== 6} onPress={submit} testID="sign-in-reset-code-submit" />
      <Links>
        <Button label={t("auth.reset.back")} variant="ghost" size="sm" onPress={() => vm.backToStart()} testID="sign-in-back" />
        <Button label={t("auth.code.resend")} variant="ghost" size="sm" loading={busy === "sendingCode"} onPress={() => void vm.forgotPassword()} testID="sign-in-reset-resend" />
      </Links>
    </Stack>
  );
}

const STRENGTH_SEGMENTS: Record<PasswordStrength, number> = { weak: 1, fair: 2, strong: 3 };

function Hint({ ok, label, testID }: { ok: boolean | undefined; label: string; testID: string }) {
  const icon: IconName = ok === false ? "close" : "check";
  return (
    <Stack direction="horizontal" gap="1.5" align="center" testID={testID}>
      <Icon name={icon} size="icon.sm" color={ok === true ? "status.success" : ok === false ? "status.danger.fg" : "text.tertiary"} />
      <Text variant="footnote" color={ok === false ? "status.danger.fg" : "text.secondary"}>
        {label}
      </Text>
    </Stack>
  );
}

/** Live hints (8+ characters, not common, not the email) and the 3-segment strength meter under a new password. */
export function PasswordHints({ password, email, refused }: { password: string; email: string; refused?: PasswordProblem }) {
  const t = useT();
  const hints = AccountSecurityLogic.passwordHints(password, email, refused);
  const strength = AccountSecurityLogic.passwordStrength(password, email);
  const filled = password === "" ? 0 : STRENGTH_SEGMENTS[strength];
  const level = strength === "weak" ? t("auth.reset.strength.weak") : strength === "fair" ? t("auth.reset.strength.fair") : t("auth.reset.strength.strong");
  const fill = strength === "weak" ? "status.danger" : strength === "fair" ? "status.warning" : "status.success";
  return (
    <Stack gap="2" testID="password-hints">
      <Stack direction="horizontal" gap="1" testID={`password-strength-${password === "" ? "none" : strength}`}>
        {[1, 2, 3].map((n) => (
          <Stack key={n} grow>
            <Stack height="dot" radius="full" background={n > filled ? "border.subtle" : fill} />
          </Stack>
        ))}
      </Stack>
      {password === "" ? null : (
        <Text variant="caption" color="text.secondary">
          {t("auth.reset.strength", { level })}
        </Text>
      )}
      <Hint ok={password === "" ? undefined : hints.length} label={t("auth.reset.hint.length")} testID="password-hint-length" />
      <Hint ok={hints.common} label={t("auth.reset.hint.common")} testID="password-hint-common" />
      <Hint ok={password === "" ? undefined : hints.email} label={t("auth.reset.hint.email")} testID="password-hint-email" />
    </Stack>
  );
}

export function ResetPasswordStep({ vm, flow, busy, error }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "resetPassword" }>; busy: Busy; error: string | undefined }) {
  const t = useT();
  const [password, setPassword] = useState("");
  const [shown, setShown] = useState(false);
  const submit = () => void vm.resetPassword(password);
  return (
    <Stack gap="4" testID="sign-in-reset-password">
      <Heading title={t("auth.reset.newTitle")} body={t("auth.reset.for", { email: flow.email })} />
      <Stack gap="2">
        <TextField
          label={t("auth.reset.newLabel")}
          value={password}
          onChange={setPassword}
          secure={!shown}
          contentType="newPassword"
          autoFocus
          disabled={busy === "resetting"}
          onSubmit={submit}
          {...(error === undefined ? {} : { error })}
          trailingAction={{
            icon: shown ? "eyeOff" : "eye",
            label: shown ? t("auth.password.hide") : t("auth.password.show"),
            onPress: () => setShown((s) => !s),
            testID: "sign-in-new-password-toggle",
          }}
          testID="sign-in-new-password"
        />
        <PasswordHints password={password} email={flow.email} {...(flow.problem === undefined ? {} : { refused: flow.problem })} />
      </Stack>
      <Stack gap="0.5">
        <Checkbox label={t("auth.reset.signOutOthers")} value={flow.signOutOthers} onChange={(v) => vm.setSignOutOthers(v)} testID="sign-in-sign-out-others" />
        <Text variant="caption" color="text.tertiary">
          {t("auth.reset.signOutHint")}
        </Text>
      </Stack>
      <Button label={t("auth.reset.save")} variant="primary" size="lg" loading={busy === "resetting"} disabled={password === ""} onPress={submit} testID="sign-in-reset-save" />
      <Stack direction="horizontal">
        <Button label={t("auth.reset.back")} variant="ghost" size="sm" onPress={() => vm.backToStart()} testID="sign-in-back" />
      </Stack>
    </Stack>
  );
}

export function ResetDoneStep({ vm, flow }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "resetDone" }> }) {
  const t = useT();
  return (
    <Stack gap="4" testID="sign-in-reset-done">
      <Stack direction="horizontal" gap="2" align="center">
        <Icon name="checkCircle" size="icon.lg" color="status.success" />
        <Text variant="headline">{t("auth.reset.doneTitle")}</Text>
      </Stack>
      <Text color="text.secondary">{flow.signedOutOthers ? t("auth.reset.doneBody", { email: flow.email }) : t("auth.reset.doneBodyKept", { email: flow.email })}</Text>
      <Button label={t("auth.reset.done")} variant="primary" size="lg" onPress={() => void vm.finishReset()} testID="sign-in-reset-continue" />
    </Stack>
  );
}

export function PasskeyStep({ vm, flow }: { vm: SessionViewModel; flow: Extract<SignInFlow, { step: "passkey" }> }) {
  const t = useT();
  if (flow.status === "waiting") {
    return (
      <Stack gap="4" align="center" testID="sign-in-passkey-waiting">
        <Spinner />
        <Stack gap="1" align="center">
          <Text variant="headline">{t("auth.passkey.waiting")}</Text>
          <Text color="text.secondary">{t("auth.passkey.waitingBody")}</Text>
        </Stack>
        <Button label={t("common.cancel")} variant="secondary" onPress={() => vm.backToStart()} testID="sign-in-passkey-cancel" />
      </Stack>
    );
  }
  const cancelled = flow.status === "cancelled";
  return (
    <Stack gap="4" testID={cancelled ? "sign-in-passkey-cancelled" : "sign-in-passkey-not-found"}>
      <Banner testID="sign-in-passkey-notice"
        tone={cancelled ? "info" : "danger"}
        icon="key"
        title={cancelled ? t("auth.passkey.cancelledTitle") : t("auth.passkey.notFoundTitle")}
        message={cancelled ? t("auth.error.passkeyCancelled") : t("auth.error.passkeyNotFound")}
      />
      <Button label={t("auth.browser.retry")} icon="key" variant="primary" size="lg" onPress={() => void vm.signInWithPasskey()} testID="sign-in-passkey-retry" />
      <Stack direction="horizontal">
        <Button label={t("auth.reset.back")} variant="ghost" size="sm" onPress={() => vm.backToStart()} testID="sign-in-back" />
      </Stack>
    </Stack>
  );
}
